import { API_HOST, STORAGE_KEY } from "@/lib/config"

const TRPC_BASE = `${API_HOST}/trpc`

async function tRPCRequest<T>(
  procedure: string,
  input?: unknown,
  method?: "GET" | "POST",
): Promise<T> {
  const token = localStorage.getItem(STORAGE_KEY)
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  }
  if (token) {
    headers["Authorization"] = `Bearer ${token}`
  }

  const url = `${TRPC_BASE}/${procedure}`
  const fetchOptions: RequestInit = { headers }

  if (method === "GET" || (method === undefined && input === undefined)) {
    fetchOptions.method = "GET"
  } else {
    fetchOptions.method = "POST"
    if (input !== undefined) {
      fetchOptions.body = JSON.stringify(input)
    }
  }

  const response = await fetch(url, fetchOptions)
  const data = await response.json()

  if (!response.ok) {
    const errorMessage = data?.error?.message || "请求失败"
    try {
      const parsed = JSON.parse(errorMessage)
      if (Array.isArray(parsed) && parsed[0]?.message) {
        throw new Error(parsed.map((e: any) => e.message).join(", "))
      }
    } catch (e) {
      // Not JSON, use as-is
    }
    throw new Error(errorMessage)
  }

  return data.result.data as T
}

export interface KnowledgePoint {
  id: number
  kp_code: string
  name: string
  parent_code?: string | null
  level?: number | null
  subject?: string | null
  stage?: string | null
  module?: string | null
  definition?: string | null
  can_do?: string | null
  hit_rules?: string[] | null
  common_errors?: string[] | null
  tags: Record<string, any>
}

export interface FilterOptions {
  stage_options: string[]
  subject_options: string[]
  module_options: string[]
}

export interface ImportResult {
  created: number
  updated: number
  skipped: number
  errors: string[]
}

export async function fetchFilterOptions(params?: {
  stage?: string
  subject?: string
}): Promise<FilterOptions> {
  return tRPCRequest<FilterOptions>("knowledgePoints.filters", params, "GET")
}

export async function importKnowledgePoints(file: File): Promise<ImportResult> {
  // Note: File upload needs to be handled differently for tRPC
  // For now, we'll read the file content and send it as a string
  const content = await file.text()
  return tRPCRequest<ImportResult>("knowledgePoints.importJsonl", { content })
}

export async function fetchKnowledgePoints(params: {
  stage?: string
  subject?: string
  module?: string
}): Promise<KnowledgePoint[]> {
  return tRPCRequest<KnowledgePoint[]>("knowledgePoints.tree", params, "GET")
}

export interface KnowledgePointPayload {
  kp_code: string
  name: string
  parent_code?: string | null
  level?: number | null
  subject?: string | null
  stage?: string | null
  module?: string | null
  definition?: string | null
  can_do?: string | null
  hit_rules?: string[] | null
  common_errors?: string[] | null
  tags?: Record<string, any>
}

export async function createKnowledgePoint(
  payload: KnowledgePointPayload,
): Promise<KnowledgePoint> {
  return tRPCRequest<KnowledgePoint>("knowledgePoints.create", payload)
}

export async function updateKnowledgePoint(
  kp_code: string,
  payload: Partial<KnowledgePointPayload>,
): Promise<KnowledgePoint> {
  return tRPCRequest<KnowledgePoint>("knowledgePoints.update", {
    kpCode: kp_code,
    ...payload,
  })
}

export async function deleteKnowledgePoint(kp_code: string): Promise<void> {
  return tRPCRequest<void>("knowledgePoints.delete", { kpCode: kp_code })
}
